import React, { useMemo, useState } from "react";
import { SegmentationItem } from "./types";
import {
  TranslationViewSegmentActions,
  AllowedIconActions,
  IconVariantState,
} from "./TranslationViewSegmentActions";
import { Spinner } from "../utils/Spinner";
import { TranslationViewSegmentRowTextarea } from "./TranslationViewSegmentRowTextarea";
import { useGetTagRulesQuery } from "../api/translationMemorySegmentTagRulesSlice";

export const copySourceToTranslationShortcut = (
  e: React.KeyboardEvent<HTMLTextAreaElement | HTMLDivElement>
): boolean => {
  const isMac =
    /Mac/i.test(navigator.platform) ||
    /Macintosh|Mac OS X/i.test(navigator.userAgent);

  if (!isMac) {
    return e.ctrlKey && e.key === "Insert";
  }
  return e.metaKey && e.key === "Enter";
};

export type Props = {
  index: number;
  segmentationItem: SegmentationItem;
  focusedSegmentIndex: number;
  showLockIcons: boolean;
  onChangeSegment: (index: number, value: string) => void;
  onToggleLockSegment: (index: number) => boolean;
  onFocusSegment: (index: number) => void;
  onCtrlEnter: (index: number) => void;
  onCtrlL: (index: number) => void;
  onCommitTranslationToMemory: (index: number) => Promise<void>;
  disabledTaggingRules: number[];
  autoFocus?: boolean;
};

export const TranslationViewSegmentRow: React.FC<Props> = ({
  index,
  segmentationItem,
  focusedSegmentIndex,
  showLockIcons,
  onChangeSegment,
  onFocusSegment,
  onCtrlEnter,
  onCtrlL,
  onToggleLockSegment,
  onCommitTranslationToMemory,
  disabledTaggingRules,
  autoFocus = false,
}) => {
  const isFocused = index === focusedSegmentIndex;
  const {
    data: tagRules,
    isLoading: isFetchingTagRules,
  } = useGetTagRulesQuery();

  const [isLoading, setIsLoading] = useState(false);

  const iconVariants = useMemo((): IconVariantState => {
    let addToTranslationMemory: IconVariantState["add-to-translation-memory"] = 0;
    if (!segmentationItem.target_text) {
      addToTranslationMemory = 2;
    } else if (segmentationItem.translation_memory_id) {
      addToTranslationMemory = 1;
    }
    let lockSegment: IconVariantState["lock-segment"] = 0;
    if (showLockIcons) {
      lockSegment = segmentationItem.locked ? 1 : 0;
    } else if (segmentationItem.locked) {
      lockSegment = 2;
    }
    return {
      "lock-segment": lockSegment,
      "add-to-translation-memory": addToTranslationMemory,
    };
  }, [segmentationItem.locked, segmentationItem]);

  const showIcons = useMemo((): AllowedIconActions[] => {
    const icons: AllowedIconActions[] = ["add-to-translation-memory"];
    if (showLockIcons) {
      icons.push("lock-segment");
    }
    if (!showLockIcons && segmentationItem.locked) {
      icons.pop();
      icons.push("lock-segment");
    }
    return icons;
  }, [
    showLockIcons,
    segmentationItem.translation_memory_id,
    segmentationItem.locked,
  ]);

  const onIconAction = (action: AllowedIconActions): void => {
    switch (action) {
      case "lock-segment":
        if (!showLockIcons) {
          return;
        }
        onToggleLockSegment(index);
        break;
      case "add-to-translation-memory": {
        setIsLoading(true);
        onCommitTranslationToMemory(index).finally(() => {
          setIsLoading(false);
        });
        break;
      }
    }
  };

  const [displaySourceText, displayTargetText] = useMemo(() => {
    const source = segmentationItem.source_text || "";
    const target = segmentationItem.target_text || "";

    if (!tagRules || isFetchingTagRules) {
      return [source, target];
    }
    return [
      tagRules.apply(source, disabledTaggingRules),
      tagRules.apply(target, disabledTaggingRules),
    ];
  }, [disabledTaggingRules, tagRules, isLoading, segmentationItem]);

  if (isLoading || isFetchingTagRules) {
    return (
      <tr
        data-testid={`translation-segment-row-${index}-loading`}
        className={`${isFocused ? "!tw-bg-blue-100" : ""} tw-text-gray-500`}
      >
        <td>{index + 1}</td>
        <td className="tw-w-1/2 tw-whitespace-pre-wrap">
          <span dangerouslySetInnerHTML={{ __html: displaySourceText }} />
        </td>
        <td className="tw-w-1/2">
          <div
            data-testid="translation-segment-loading"
            className="tw-flex tw-gap-2 tw-items-center"
          >
            <Spinner />
            <span className="tw-text-gray-500 tw-whitespace-pre-wrap">
              <span
                dangerouslySetInnerHTML={{
                  __html: displayTargetText,
                }}
              />
            </span>
          </div>
        </td>
      </tr>
    );
  }
  return (
    <tr
      data-testid={`translation-segment-row-${index}`}
      className={`${isFocused ? "!tw-bg-blue-100" : ""}`}
    >
      <td>{segmentationItem.display_index || index + 1}</td>
      <td
        data-testid={`translation-segment-source-${index}`}
        className="tw-w-1/2 tw-whitespace-pre-wrap"
      >
        <span dangerouslySetInnerHTML={{ __html: displaySourceText }} />
      </td>
      <td className="tw-w-1/2">
        <div className={"tw-flex tw-gap-1 tw-items-center"}>
          <TranslationViewSegmentActions
            onAction={onIconAction}
            allowedActions={showIcons}
            showIconVariants={iconVariants}
            index={index}
          />
          <TranslationViewSegmentRowTextarea
            index={index}
            onChangeSegment={onChangeSegment}
            onFocusSegment={onFocusSegment}
            onCtrlL={onCtrlL}
            onCtrlEnter={onCtrlEnter}
            autoFocus={autoFocus}
            showLockIcons={showLockIcons}
            rawSourceText={segmentationItem.source_text}
            displayTargetText={displayTargetText}
            isLocked={segmentationItem.locked}
          />
        </div>
      </td>
    </tr>
  );
};
